import React, { useState } from 'react';
import './RecipeCard.css';

// Toast Notification Component
const Toast = ({ message, show, onClose }) => {
  React.useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000); // Auto-close after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

 return (
  <div style={{
    position: 'relative',
    margin: '20px 0',
    backgroundColor: '#4ECDC4',
    color: 'white',
    padding: '20px 30px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    zIndex: 999999,  // Changed from 10000 to 999999
    maxWidth: '400px',
    animation: 'slideIn 0.4s ease-out',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}>
        <span style={{ fontSize: '24px' }}>✅</span>
        <div style={{
            fontSize: '16px',
            lineHeight: '1.5',
            whiteSpace: 'pre-line'
          }}>
            {message}
          </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '0',
            lineHeight: '1'
          }}
        >
          ×
        </button>
      </div>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

function RecipeCard({ recipe, onReset, userEmail, hasEmail, onEmailRequest }) {
  const [currentImageUrl, setCurrentImageUrl] = useState(recipe.imageUrl);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // TEST - Remove this after testing
React.useEffect(() => {
  console.log('Toast state changed:', { showToast, toastMessage });
}, [showToast, toastMessage]);

  // Show toast notification
  const showNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  // Copy text to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      return false;
    }
  };

  const handleEmailRecipe = () => {
    if (!hasEmail) {
      onEmailRequest();
      return;
    }
    
    const subject = encodeURIComponent(`Your RYSL Mocktail: ${recipe.name}`);
    const body = encodeURIComponent(formatRecipeForEmail(recipe));
    window.location.href = `mailto:${userEmail}?subject=${subject}&body=${body}`;
  };

  const handleDownloadImage = async () => {
    try {
      const response = await fetch(currentImageUrl, {
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${recipe.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-mocktail.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Download error:', error);
      return false;
    }
  };

  // Generate social media caption with hashtags
  const generateSocialCaption = () => {
    let caption = `Look what I just created! 🍹\n\n${recipe.name}\n\n`;
    
    if (recipe.description) {
      caption += `${recipe.description}\n\n`;
    }
    
    caption += `THE RYSL 3 F'S METHOD:\n\n`;
    
    if (recipe.fizz && recipe.fizz.length > 0) {
      caption += `🫧 FIZZ\n${recipe.fizz.map(item => `• ${item}`).join('\n')}\n\n`;
    }
    
    if (recipe.flavor && recipe.flavor.length > 0) {
      caption += `🍋 FLAVOR\n${recipe.flavor.map(item => `• ${item}`).join('\n')}\n\n`;
    }
    
    if (recipe.finish && recipe.finish.length > 0) {
      caption += `🌿 FINISH\n${recipe.finish.map(item => `• ${item}`).join('\n')}\n\n`;
    }
    
    caption += `#RockYourSoberLife #SoberLife #Mocktails #SoberCurious #AlcoholFree #MocktailRecipe\n\n---\nMix your own at ryslmixmaster.com`;
    
    return caption;
  };

  // Download image and copy caption for social sharing
  const handleDownloadAndCopy = async () => {
    if (!hasEmail) {
      onEmailRequest();
      return;
    }
    
    const caption = generateSocialCaption();
    
    // Download image
    const downloaded = await handleDownloadImage();
    
    // Copy caption
    const copied = await copyToClipboard(caption);
    
    if (downloaded && copied) {
      showNotification('Recipe copied & image downloaded! Go share on your favorite social platforms!');
    } else if (downloaded) {
      showNotification('✅ Image downloaded! Caption ready to copy below.');
      alert(`Copy this caption:\n\n${caption}`);
    } else {
      showNotification('❌ Download failed. Please try again.');
    }
  };

  // Copy just the recipe text (no hashtags)
  const handleCopyRecipe = async () => {
    if (!hasEmail) {
      onEmailRequest();
      return;
    }
    
    const recipeText = formatRecipeForEmail(recipe);
    const copied = await copyToClipboard(recipeText);
    
    if (copied) {
      showNotification('✅ Recipe copied to clipboard!');
    } else {
      alert(`Copy this recipe:\n\n${recipeText}`);
    }
  };

  // Print recipe
  const handlePrintRecipe = () => {
    if (!hasEmail) {
      onEmailRequest();
      return;
    }
    
    const printWindow = window.open('', '_blank');
    const recipeHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${recipe.name} - RYSL MixMaster Studio</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            max-width: 800px;
            margin: 40px auto;
            padding: 20px;
            line-height: 1.6;
          }
          h1 {
            color: #2C3E50;
            text-align: center;
            margin-bottom: 10px;
          }
          .description {
            text-align: center;
            color: #7f8c8d;
            font-style: italic;
            margin-bottom: 30px;
          }
          h2 {
            color: #34495E;
            margin-top: 30px;
            margin-bottom: 15px;
            border-bottom: 2px solid #4ECDC4;
            padding-bottom: 5px;
          }
          h3 {
            color: #2C3E50;
            margin-top: 20px;
            margin-bottom: 10px;
          }
          ul, ol {
            margin: 10px 0;
            padding-left: 30px;
          }
          li {
            margin: 8px 0;
          }
          .pro-tip {
            background: #FFF3CD;
            border-left: 4px solid #FFB347;
            padding: 15px;
            margin: 20px 0;
            border-radius: 5px;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            color: #7f8c8d;
          }
          @media print {
            body { margin: 20px; }
          }
        </style>
      </head>
      <body>
        <h1>🍹 ${recipe.name}</h1>
        ${recipe.description ? `<p class="description">${recipe.description}</p>` : ''}
        
        <h2>THE RYSL 3 F'S METHOD</h2>
        
        ${recipe.fizz && recipe.fizz.length > 0 ? `
          <h3>🫧 FIZZ</h3>
          <ul>
            ${recipe.fizz.map(item => `<li>${item}</li>`).join('')}
          </ul>
        ` : ''}
        
        ${recipe.flavor && recipe.flavor.length > 0 ? `
          <h3>🍋 FLAVOR</h3>
          <ul>
            ${recipe.flavor.map(item => `<li>${item}</li>`).join('')}
          </ul>
        ` : ''}
        
        ${recipe.finish && recipe.finish.length > 0 ? `
          <h3>🌿 FINISH</h3>
          <ul>
            ${recipe.finish.map(item => `<li>${item}</li>`).join('')}
          </ul>
        ` : ''}
        
        <h2>📝 INSTRUCTIONS</h2>
        <ol>
          ${recipe.instructions.map(step => `<li>${step}</li>`).join('')}
        </ol>
        
        ${recipe.proTip ? `
          <div class="pro-tip">
            <strong>💡 PRO TIP:</strong> ${recipe.proTip}
          </div>
        ` : ''}
        
        <div class="footer">
          Created with RYSL MixMaster Studio<br>
          ryslmixmaster.com
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(recipeHTML);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleRegenerateImage = async () => {
    setIsRegenerating(true);
    try {
       const apiUrl = process.env.REACT_APP_API_URL || 'https://rysl-mixmaster-studio.onrender.com';
       const response = await fetch(`${apiUrl}/api/regenerate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recipe }),
      });

      if (!response.ok) {
        throw new Error('Failed to regenerate image');
      }

      const data = await response.json();
      setCurrentImageUrl(data.imageUrl);
      showNotification('✨ New image generated!');
    } catch (error) {
      console.error('Error regenerating image:', error);
      showNotification('❌ Failed to regenerate image. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const formatRecipeForEmail = (recipe) => {
    let text = `${recipe.name}\n\n`;
    
    if (recipe.description) {
      text += `${recipe.description}\n\n`;
    }
    
    text += `THE RYSL 3 F'S METHOD:\n\n`;
    
    if (recipe.fizz && recipe.fizz.length > 0) {
      text += `🫧 FIZZ\n`;
      recipe.fizz.forEach(item => text += `• ${item}\n`);
      text += `\n`;
    }
    
    if (recipe.flavor && recipe.flavor.length > 0) {
      text += `🍋 FLAVOR\n`;
      recipe.flavor.forEach(item => text += `• ${item}\n`);
      text += `\n`;
    }
    
    if (recipe.finish && recipe.finish.length > 0) {
      text += `🌿 FINISH\n`;
      recipe.finish.forEach(item => text += `• ${item}\n`);
      text += `\n`;
    }
    
    text += `📝 INSTRUCTIONS:\n`;
    recipe.instructions.forEach((step, index) => {
      text += `${index + 1}. ${step}\n`;
    });
    
    if (recipe.proTip) {
      text += `\n💡 PRO TIP: ${recipe.proTip}\n`;
    }
    
    text += `\n---\nCreated with RYSL MixMaster Studio\nryslmixmaster.com`;
    
    return text;
  };

  return (
  <>
    <div className="recipe-display-wrapper">

      {/* IMAGE SECTION */}
      {currentImageUrl && (
        <div className="recipe-hero-image">
          <img src={currentImageUrl} alt={recipe.name} />
          {isRegenerating && (
            <div className="image-regenerating-overlay">
              <div className="spinner"></div>
              <p className="regenerating-main-text">Hold tight!</p>
              <p className="regenerating-sub-text">Creating your new image takes about 15-20 seconds... ✨</p>
            </div>
          )}
          {currentImageUrl && (
            <button 
              className="regenerate-image-button" 
              onClick={handleRegenerateImage}
              disabled={isRegenerating}
            >
              {isRegenerating ? '⏳ Generating...' : '🎨 Regenerate Image'}
            </button>
          )}
        </div>
      )}

      {/* RECIPE CARD */}
      <div className="recipe-card-container">
        <div className="recipe-content">
          <h2 className="recipe-name">🍹 {recipe.name}</h2>
          
          {recipe.description && (
            <p className="recipe-description">{recipe.description}</p>
          )}
          
          <div className="four-fs-section">
            <h3 className="section-title">THE RYSL 3 F'S METHOD:</h3>
            
            {recipe.fizz && recipe.fizz.length > 0 && (
              <div className="f-section">
                <h4 className="f-title">🫧 FIZZ</h4>
                <ul className="ingredient-list">
                  {recipe.fizz.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {recipe.flavor && recipe.flavor.length > 0 && (
              <div className="f-section">
                <h4 className="f-title">🍋 FLAVOR</h4>
                <ul className="ingredient-list">
                  {recipe.flavor.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {recipe.finish && recipe.finish.length > 0 && (
              <div className="f-section">
                <h4 className="f-title">🌿 FINISH</h4>
                <ul className="ingredient-list">
                  {recipe.finish.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="instructions-section">
            <h3 className="section-title">📝 INSTRUCTIONS:</h3>
            <ol className="instructions-list">
              {recipe.instructions.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>

          {recipe.proTip && (
            <div className="pro-tip">
              <strong>💡 PRO TIP:</strong> {recipe.proTip}
            </div>
          )}

          {/* NEW BUTTON ORDER */}
          <div className="action-buttons">
            {/* 1. Mix Another One - Full Width */}
            <button className="action-button reset-button" onClick={onReset}>
              🍹 Mix Another One
            </button>

            {/* 2. Download & Share - Full Width */}
			{/* Toast notification - shows right above the button */}
			<Toast 
			 message={toastMessage}
			 show={showToast}
			 onClose={() => setShowToast(false)}
			/>

            <button 
              className={`action-button share-button ${!hasEmail ? 'locked' : ''}`}
              onClick={handleDownloadAndCopy}
              title={!hasEmail ? "Enter email to unlock Save & Share" : ""}
            >
              {!hasEmail && '🔒 '}📱 Download Image & Copy Recipe to Share on Socials
            </button>

            {/* 3. Utility Buttons - 3 Equal Columns */}
            <div className="utility-buttons">
              <button 
                className={`action-button print-button ${!hasEmail ? 'locked' : ''}`}
                onClick={handlePrintRecipe}
                title={!hasEmail ? "Enter email to unlock Print" : ""}
              >
                {!hasEmail && '🔒 '}🖨️ Print
              </button>
              <button 
                className={`action-button copy-button ${!hasEmail ? 'locked' : ''}`}
                onClick={handleCopyRecipe}
                title={!hasEmail ? "Enter email to unlock Copy" : ""}
              >
                {!hasEmail && '🔒 '}📋 Copy
              </button>
              <button 
                className={`action-button email-button ${!hasEmail ? 'locked' : ''}`}
                onClick={handleEmailRecipe}
                title={!hasEmail ? "Enter email to unlock Email" : ""}
              >
                {!hasEmail && '🔒 '}📧 Email
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
	</>
   );
}

export default RecipeCard;